import { FunctionComponent, PropsWithChildren } from 'react';

import { cn } from '../../../lib/css/className/className';
import { TestProps } from '../../../internal/types/testTypes';
import './MediaControl.css';

const cls = cn('MediaControl');

type HorizontalPosition = 'left' | 'right';
type VerticalPosition = 'top' | 'bottom';

export type MediaControlPosition = 'center' | `${VerticalPosition}-${HorizontalPosition}`;

export interface MediaControlProps extends TestProps {
    /**
     * Позиция контрола внутри MediaItem
     * @default: 'top-left'
     */
    position?: MediaControlPosition;
    /**
     * Показывать ли контрол всегда, по-умолчанию виден только при наведении на MediaItem
     * @default: false
     */
    isAlwaysVisible?: boolean;
}

/**
 * Базовый контрол для `<MediaItem />`, используется для создания своих контролов
 *
 * `<MediaControl />` в первую очередь предназначен для отображения следующих компонентов
 *
 * - `<FloatingButton />`:
 *
 * ```tsx
 *  <MediaControl position="center">
 *      <FloatingButton size="l" color="contrast" icon={IconPlayFilled} />
 *  </MediaControl>
 *  ```
 *
 * - `<Label />`:
 *
 * ```tsx
 *  <MediaControl position="bottom-left">
 *      <Label color="black-negative" size="4xs">
 *          Видеодополнение
 *      </Label>
 *  </MediaControl>
 *  ```
 *
 * - `<ClickableIcon />`:
 *
 * ```tsx
 *  <MediaControl position="top-left">
 *      <ClickableIcon>
 *          <IconSelectYellowFilled size="24" />
 *      </ClickableIcon>
 *  </MediaControl>
 *  ```
 */
export const MediaControl: FunctionComponent<PropsWithChildren<MediaControlProps>> = props => {
    const { 'data-testid': dataTestId = 'MediaControl', position = 'top-left', isAlwaysVisible, children } = props;

    const [verticalPosition, horizontalPosition] = position === 'center' ? ['center', 'center'] : position.split('-');

    return (
        <div
            data-testid={dataTestId}
            className={cls({
                horizontalPosition,
                verticalPosition,
                visible: isAlwaysVisible,
            })}
        >
            {children}
        </div>
    );
};
